import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ScheduleIcon from '@material-ui/icons/Schedule';
import TrafficIcon from '@material-ui/icons/Traffic';
import PersonIcon from '@material-ui/icons/Person';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import _ from '../../../../utils/lodash';
import useTimeline from '../../timeline/useTimeline';
import { startEndTimeDisplay, isBetweenDateRange, timeIsBetweenDateRange } from '../../../../utils/dates';
import { getAvailableTimeSpansForTreatment } from '../../../../actions/areas/planner';
import { postAssignClinicianToTreatment } from '../../../../actions/areas/planner/commandActions';
import { overrideTreatmentInstance } from '../../../../actions/entities/treatmentInstances/addOverride';

export default function CopyTreatment({ settings, baseRequestModel }) {
  const dispatch = useDispatch();
  const {
    activeAssignment: assignment,
    activeTreatmentInstanceForCopy: treatmentInstance,
    setActiveTreatmentInstanceForCopy,
    activeUnassignedTreatment: unassignedTreatmentInstance,
    refreshClinicianTreatmentsAndClosePanels,
    closeCopyPanels,
    getAccessToken,
    setAlertMessage,
  } = useTimeline();

  const [componentState, setComponentState] = useState({
    assignableTimeSpan: null,
    selectedClinician: null,
    selectedAvailability: null,
    isCopying: false,
  });

  const clinicians = settings.unfilteredClinicians;

  if ((!assignment && !unassignedTreatmentInstance) || !treatmentInstance) return null;

  const handleClinicianChange = async e => {
    const clinicianId = e.target.value;
    if (clinicianId === '') {
      setComponentState({
        ...componentState,
        assignableTimeSpan: null,
        selectedClinician: null,
        selectedAvailability: null,
        isCopying: false,
      });
      return;
    }
    setComponentState({
      ...componentState,
      isCopying: true,
    });
    const clinician = clinicians.find(item => item.id === clinicianId);
    const availability = clinician.availabilities.find(a => isBetweenDateRange(assignment.visitTime.actual, a.actual));

    const setClinicianAndTimeSpan = timeSpans => {
      const firstTimeSpan = timeSpans
        ? timeSpans[0].timeSpans.find(
            span =>
              timeIsBetweenDateRange(
                { dateFrom: span.start, dateTo: span.end },
                assignment.visitTime.actual.startDate,
              ) && span.isAssignablePeriod,
          )
        : null;
      setComponentState({
        ...componentState,
        assignableTimeSpan: firstTimeSpan,
        selectedClinician: clinician,
        selectedAvailability: availability,
        isCopying: false,
      });
    };

    await getAvailableTimeSpansForTreatment(
      getAccessToken,
      _.merge(
        {
          ClinicianIds: [clinicianId],
          TreatmentId: treatmentInstance.treatmentId,
          TimeSlotId: assignment.timeSlotId,
          IsCopyVisit: true,
        },
        baseRequestModel,
      ),
      false,
      setClinicianAndTimeSpan,
    );
  };

  const closePanelAndRefresh = () => {
    closeCopyPanels();
    settings.toggleForceRefresh();
  };

  const errorCallback = error => {
    setAlertMessage(error);
    setComponentState({ ...componentState, isCopying: false });
  };

  const assignClinicianToTreatment = copiedTreatment => {
    const assignmentParams = {
      treatmentId: copiedTreatment.treatmentId,
      treatmentInstanceId: copiedTreatment.treatmentInstanceId !== null ? copiedTreatment.treatmentInstanceId : -1,
    };
    const model = _.merge(assignmentParams, baseRequestModel, {
      assignmentTime: assignment.visitTime.actual.startDate,
      postingId: componentState.selectedAvailability.postingId,
    });
    postAssignClinicianToTreatment(getAccessToken, model, closePanelAndRefresh, errorCallback);
  };

  const copyTreatment = () => {
    setComponentState({ ...componentState, isCopying: true });
    return dispatch(
      overrideTreatmentInstance(
        treatmentInstance.treatmentId,
        treatmentInstance.treatmentInstanceId,
        treatmentInstance.occurrence,
        treatmentInstance.timeSlots,
      ),
    )
      .then(() => {
        if (settings.includeTreatments) closePanelAndRefresh();
        else refreshClinicianTreatmentsAndClosePanels();
      })
      .finally(() => setComponentState({ ...componentState, isCopying: false }));
  };

  const copyAssignedTreatment = () => {
    setComponentState({ ...componentState, isCopying: true });
    const openCopyPanel = data => {
      const copiedTreatment = data.response.data.responseTreatment;
      if (copiedTreatment.masterId === null || copiedTreatment.isMasterAssigned) {
        setActiveTreatmentInstanceForCopy(copiedTreatment);
        if (componentState.selectedClinician) {
          assignClinicianToTreatment(copiedTreatment);
        } else {
          closePanelAndRefresh();
        }
      }
    };

    const timeSlots = treatmentInstance.timeSlots;
    return dispatch(
      overrideTreatmentInstance(
        treatmentInstance.treatmentId,
        treatmentInstance.treatmentInstanceId,
        treatmentInstance.occurrence,
        timeSlots,
        assignment.id,
      ),
    ).then(data => openCopyPanel(data));
  };

  const headerInfo = () => {
    return (
      <div className="grey-sidePanel-header">
        <div className="sidePanel-header-subtitle">
          <Box flexGrow={1} py={1}>
            <Typography variant="subtitle1">Copy visit</Typography>
          </Box>
        </div>
        <div className="sidePanel-header-title-wrap">
          <div className="sidePanel-header-title">
            <Typography variant="h2">
              {unassignedTreatmentInstance ? unassignedTreatmentInstance.patientName : assignment.secondaryLabel}
            </Typography>
            <Typography variant="h3" gutterBottom>
              {unassignedTreatmentInstance ? unassignedTreatmentInstance.postcode : assignment.postcode}
            </Typography>
            <Typography variant="h5">Any Grade</Typography>
          </div>
          <div className="sidePanel-header-info">
            <div className="sidePanel-header-info patient-header">
              <List component="nav">
                <div className="patient-info-details">
                  {!unassignedTreatmentInstance && (
                    <ListItem key={'time-info'}>
                      <ListItemIcon>
                        <ScheduleIcon />
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          assignment.visitTime
                            ? startEndTimeDisplay(
                                assignment.visitTime.actual.startDate,
                                assignment.visitTime.actual.endDate,
                              )
                            : assignment.label
                        }
                      />
                    </ListItem>
                  )}
                  <ListItem key={'visit-status'}>
                    <ListItemIcon>
                      <TrafficIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        unassignedTreatmentInstance ? unassignedTreatmentInstance.postcode : assignment.visitStatus
                      }
                    />
                  </ListItem>
                </div>
                <div className="patient-info-details">
                  {!unassignedTreatmentInstance && (
                    <ListItem key={'travel-time'}>
                      <ListItemIcon>
                        <DirectionsCarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          assignment.travelTime
                            ? startEndTimeDisplay(
                                assignment.travelTime.actual.startDate,
                                assignment.travelTime.actual.endDate,
                              )
                            : ''
                        }
                      />
                    </ListItem>
                  )}
                  <ListItem key={'required-people'}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        unassignedTreatmentInstance
                          ? unassignedTreatmentInstance.numberOfClinicians
                          : assignment.numberOfClinicians
                      }
                    />
                  </ListItem>
                </div>
              </List>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const mainContent = () => {
    return (
      <div className="copy-sidePanel-form">
        <Select multiple={false} onChange={e => handleClinicianChange(e)}>
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {clinicians &&
            clinicians.map(clinician => (
              <MenuItem key={clinician.id} value={clinician.id}>
                {clinician.name}
              </MenuItem>
            ))}
        </Select>
      </div>
    );
  };

  const actionButtons = () => {
    const clinicianIsNotAvailable =
      componentState.selectedClinician && (!componentState.assignableTimeSpan || !componentState.selectedAvailability);
    return (
      <div className="sidePanel-footer">
        {clinicianIsNotAvailable && (
          <div className="validation-error">* The selected clinician cannot be assigned to this visit.</div>
        )}
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setActiveTreatmentInstanceForCopy(null);
              }}
            >
              Back
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            {!unassignedTreatmentInstance ? (
              <Button
                variant="contained"
                color="primary"
                disabled={componentState.isCopying || (componentState.selectedClinician && clinicianIsNotAvailable)}
                onClick={() => {
                  copyAssignedTreatment();
                }}
              >
                Save
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={componentState.isCopying}
                onClick={() => {
                  copyTreatment();
                }}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open={treatmentInstance !== null}
        onOpen={() => {}}
        onClose={() => {
          setActiveTreatmentInstanceForCopy(null);
        }}
      >
        <div className="sidePanel-wrapper" style={{ width: '350px' }} id="copy-side-panel">
          {headerInfo()}
          {!unassignedTreatmentInstance && mainContent()}
          {actionButtons()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
